<template>
	<el-dialog class="forward-dialog" :title="dialogTitle" :visible.sync="visible" width="400px" append-to-body
		:before-close="onClose">
		<el-input class="fwd-search" size="small" placeholder="搜索联系人 / 群聊" prefix-icon="el-icon-search"
			v-model="searchText"></el-input>
		<el-scrollbar class="fwd-list">
			<div v-for="t in targetList" :key="t.key" class="fwd-item" :class="{ active: selectedKeys.includes(t.key) }"
				@click="toggle(t)">
				<el-checkbox class="fwd-check" :value="selectedKeys.includes(t.key)"
					@click.native.stop="toggle(t)"></el-checkbox>
				<head-image class="fwd-avatar" :name="t.showName" :size="36" :url="t.headImage"></head-image>
				<span class="fwd-name">{{ t.showName }}</span>
				<span class="fwd-tag" v-if="t.type == $enums.CONVERSATION_TYPE.GROUP">群</span>
			</div>
			<div v-if="!targetList.length" class="fwd-empty">无匹配结果</div>
		</el-scrollbar>
		<span slot="footer" class="dialog-footer">
			<span class="fwd-count">已选 {{ selectedKeys.length }}</span>
			<el-button size="small" @click="onClose">取消</el-button>
			<el-button size="small" type="primary" :disabled="!selectedKeys.length" @click="onConfirm">发送</el-button>
		</span>
	</el-dialog>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
export default {
	name: "forwardDialog",
	components: { HeadImage },
	props: {
		dialogVisible: {
			type: Boolean,
			default: false
		},
		// 'each' 逐条转发 | 'merge' 合并转发
		mode: {
			type: String,
			default: 'each'
		},
		count: {
			type: Number,
			default: 1
		}
	},
	data() {
		return {
			searchText: '',
			selectedKeys: []
		}
	},
	methods: {
		toggle(t) {
			const idx = this.selectedKeys.indexOf(t.key);
			if (idx >= 0) {
				this.selectedKeys.splice(idx, 1);
			} else {
				this.selectedKeys.push(t.key);
			}
		},
		onClose() {
			this.$emit("close");
		},
		onConfirm() {
			if (!this.selectedKeys.length) {
				return;
			}
			const keySet = new Set(this.selectedKeys);
			const targets = this.targetList.filter(t => keySet.has(t.key)).map(t => ({
				type: t.type,
				targetId: t.targetId,
				showName: t.showName,
				headImage: t.headImage
			}));
			this.$emit("confirm", targets);
		}
	},
	computed: {
		visible: {
			get() {
				return this.dialogVisible;
			},
			set(val) {
				if (!val) {
					this.onClose();
				}
			}
		},
		dialogTitle() {
			const t = this.mode == 'merge' ? '合并转发给' : '转发给';
			return this.count > 1 ? `${t}（共${this.count}条）` : t;
		},
		targetList() {
			const P = this.$enums.CONVERSATION_TYPE.PRIVATE;
			const G = this.$enums.CONVERSATION_TYPE.GROUP;
			const map = new Map();
			// 临时外部客户不可作为转发目标: type=99 芝麻影子买家, userName带guest_前缀的扫码访客。
			// 注意: type=1 是普通用户(建表注释), 绝不能当过滤条件(曾误加导致普通好友全被排除)
			const isTempUser = (id) => {
				const f = this.friendStore.findFriend(id);
				return !!f && (f.type === 99 || (f.userName || '').indexOf('guest_') === 0);
			};
			// 最近会话优先
			this.chatStore.conversations.forEach(c => {
				if (c.type != P && c.type != G) {
					return;
				}
				if (c.type == P && isTempUser(c.targetId)) {
					return;
				}
				const key = c.type + '_' + c.targetId;
				if (!map.has(key)) {
					map.set(key, {
						key, type: c.type, targetId: c.targetId,
						showName: c.showName, headImage: c.headImage
					});
				}
			});
			// 好友
			this.friendStore.friends.forEach(f => {
				if (f.deleted || f.type === 99 || (f.userName || '').indexOf('guest_') === 0) {
					return;
				}
				const key = P + '_' + f.id;
				if (!map.has(key)) {
					map.set(key, {
						key, type: P, targetId: f.id,
						showName: f.nickName, headImage: f.headImage
					});
				}
			});
			// 群聊
			this.groupStore.groups.forEach(g => {
				if (g.quit || g.dissolve) {
					return;
				}
				const key = G + '_' + g.id;
				if (!map.has(key)) {
					map.set(key, {
						key, type: G, targetId: g.id,
						showName: g.showGroupName || g.remarkGroupName || g.name,
						headImage: g.headImageThumb || g.headImage
					});
				}
			});
			let list = Array.from(map.values());
			const kw = this.searchText.trim().toLowerCase();
			if (kw) {
				list = list.filter(t => (t.showName || '').toLowerCase().includes(kw));
			}
			return list;
		}
	},
	watch: {
		dialogVisible(val) {
			if (val) {
				// 每次打开重置选择
				this.searchText = '';
				this.selectedKeys = [];
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.forward-dialog {
	.fwd-search {
		margin-bottom: 10px;
	}

	.fwd-list {
		height: 320px;

		.fwd-item {
			display: flex;
			align-items: center;
			padding: 6px 8px;
			border-radius: 6px;
			cursor: pointer;

			&:hover {
				background: var(--im-background-active);
			}

			&.active {
				background: var(--im-color-primary-light-9, #ecf5ff);
			}

			.fwd-check {
				margin-right: 8px;
			}

			.fwd-avatar {
				flex-shrink: 0;
				margin-right: 10px;
			}

			.fwd-name {
				flex: 1;
				min-width: 0;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
				font-size: 14px;
			}

			.fwd-tag {
				flex-shrink: 0;
				margin-left: 6px;
				padding: 0 6px;
				border-radius: 4px;
				font-size: 12px;
				color: var(--im-color-primary, #409EFF);
				background: var(--im-color-primary-light-9, #ecf5ff);
			}
		}

		.fwd-empty {
			padding: 40px 0;
			text-align: center;
			color: var(--im-text-color-light);
			font-size: 13px;
		}
	}

	.fwd-count {
		float: left;
		line-height: 32px;
		font-size: 13px;
		color: var(--im-text-color-light);
	}
}
</style>
